/**
 * E-Mail-Vorlagen für Dokumenttypen, die nur per E-Mail versendet werden (nicht in Textvorlagen-UI).
 * Speicherung: document_text_templates.send_pdf_email_*.
 */

export const DOCUMENT_TYPES_EMAIL_TEMPLATE = ['Arbeitsbericht'] as const;

export type EmailTemplateDocumentType = (typeof DOCUMENT_TYPES_EMAIL_TEMPLATE)[number];

export function isEmailTemplateDocumentType(value: unknown): value is EmailTemplateDocumentType {
  return typeof value === 'string' && (DOCUMENT_TYPES_EMAIL_TEMPLATE as readonly string[]).includes(value);
}

export const WORK_REPORT_DOCUMENT_TYPE = 'Arbeitsbericht' satisfies EmailTemplateDocumentType;

export const DEFAULT_WORK_REPORT_SIGNER_EMAIL_SUBJECT_TEMPLATE =
  'Ihr Arbeitsbericht — {{Belegnummer}}';

export const DEFAULT_WORK_REPORT_SIGNER_EMAIL_BODY_TEMPLATE = [
  '{{Anrede}}',
  '',
  'anbei erhalten Sie den unterzeichneten Arbeitsbericht {{Belegnummer}} vom {{Datum}}.',
  '',
  'Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.',
].join('\n');

/** Signer copy for a standalone checklist protocol PDF (not stored in document_text_templates). */
export const DEFAULT_CHECKLIST_SIGNER_EMAIL_SUBJECT_TEMPLATE =
  'Ihr Protokoll — {{Belegnummer}}';

export const DEFAULT_CHECKLIST_SIGNER_EMAIL_BODY_TEMPLATE = [
  '{{Anrede}}',
  '',
  'anbei erhalten Sie das unterzeichnete Protokoll {{Belegnummer}} vom {{Datum}}.',
  '',
  'Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.',
].join('\n');

/** Signer copy for a field-app invoice PDF (uses invoice mail dispatch; not Arbeitsbericht templates). */
export const DEFAULT_INVOICE_SIGNER_EMAIL_SUBJECT_TEMPLATE =
  'Ihre Rechnung — {{Belegnummer}}';

export const DEFAULT_INVOICE_SIGNER_EMAIL_BODY_TEMPLATE = [
  '{{Anrede}}',
  '',
  'anbei erhalten Sie die unterzeichnete Rechnung {{Belegnummer}} vom {{Datum}}.',
  '',
  'Bei Rückfragen stehen wir Ihnen gerne zur Verfügung.',
].join('\n');

export type DocumentEmailTemplateValues = {
  documentReference: string;
  dateLabel: string;
  customerName: string;
  signerName: string;
};

/** Platzhalter, wie sie in der UI und in neuen Vorlagen stehen: deutsche, lesbare Namen. */
export const DOCUMENT_EMAIL_PLACEHOLDER_NAMES = {
  greeting: 'Anrede',
  signerName: 'Unterzeichner',
  documentReference: 'Belegnummer',
  dateLabel: 'Datum',
  customerName: 'Kunde',
} as const;

export type DocumentEmailPlaceholderKey = keyof typeof DOCUMENT_EMAIL_PLACEHOLDER_NAMES;

/**
 * Erlaubte Schreibweisen pro Wert. Die englischen Tokens sind Altbestand und
 * bleiben gueltig, damit bereits gespeicherte Vorlagen weiter ersetzt werden.
 */
const PLACEHOLDER_ALIASES: Record<DocumentEmailPlaceholderKey, readonly string[]> = {
  greeting: [DOCUMENT_EMAIL_PLACEHOLDER_NAMES.greeting, 'GREETING'],
  signerName: [DOCUMENT_EMAIL_PLACEHOLDER_NAMES.signerName, 'SIGNER_NAME'],
  documentReference: [DOCUMENT_EMAIL_PLACEHOLDER_NAMES.documentReference, 'DOCUMENT_REFERENCE'],
  dateLabel: [DOCUMENT_EMAIL_PLACEHOLDER_NAMES.dateLabel, 'DATE'],
  customerName: [DOCUMENT_EMAIL_PLACEHOLDER_NAMES.customerName, 'CUSTOMER_NAME'],
};

/** `{{Name}}` in der Vorlage — Gross-/Kleinschreibung und Leerzeichen egal. */
function placeholderPattern(name: string): RegExp {
  return new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'gi');
}

/** Ersetzt per Funktion, damit `$`-Sequenzen im Wert nicht als Muster gelesen werden. */
function replacePlaceholder(
  text: string,
  key: DocumentEmailPlaceholderKey,
  value: string,
): string {
  return PLACEHOLDER_ALIASES[key].reduce(
    (acc, name) => acc.replace(placeholderPattern(name), () => value),
    text,
  );
}

/** Platzhalter in seiner Schreibweise fuer die UI, z. B. `{{Belegnummer}}`. */
export function formatDocumentEmailPlaceholder(key: DocumentEmailPlaceholderKey): string {
  return `{{${DOCUMENT_EMAIL_PLACEHOLDER_NAMES[key]}}}`;
}

const ANY_PLACEHOLDER_PATTERN = /\{\{\s*[A-Za-z0-9_äöüÄÖÜß]+\s*\}\}/g;

/** Platzhalter-Name (deutsch oder altes englisches Token) auf seinen Wert abbilden. */
export function resolveDocumentEmailPlaceholderKey(name: string): DocumentEmailPlaceholderKey | null {
  const needle = name.trim().toLowerCase();
  const keys = Object.keys(PLACEHOLDER_ALIASES) as DocumentEmailPlaceholderKey[];
  return keys.find((key) => PLACEHOLDER_ALIASES[key].some((alias) => alias.toLowerCase() === needle)) ?? null;
}

/** Alte englische Tokens auf die deutschen Namen umschreiben, Unbekanntes bleibt stehen. */
export function normalizeDocumentEmailPlaceholders(text: string): string {
  return text.replace(/\{\{\s*([A-Za-z0-9_äöüÄÖÜß]+)\s*\}\}/g, (match, name: string) => {
    const key = resolveDocumentEmailPlaceholderKey(name);
    return key ? formatDocumentEmailPlaceholder(key) : match;
  });
}

/**
 * Platzhalter, die der Versand nicht ersetzen kann — Tippfehler oder erfundene
 * Namen. `allowGreeting: false` fuer den Betreff, dort gibt es keine Anrede.
 */
export function findUnknownDocumentEmailPlaceholders(
  text: string,
  options: { allowGreeting: boolean },
): string[] {
  const keys = Object.keys(PLACEHOLDER_ALIASES) as DocumentEmailPlaceholderKey[];
  const allowed = keys
    .filter((key) => options.allowGreeting || key !== 'greeting')
    .flatMap((key) => PLACEHOLDER_ALIASES[key].map((name) => name.toLowerCase()));
  const found = text.match(ANY_PLACEHOLDER_PATTERN) ?? [];
  return Array.from(new Set(found)).filter(
    (token) => !allowed.includes(token.replace(/[{}\s]/g, '').toLowerCase()),
  );
}

const DEFAULT_SUBJECT_BY_TYPE: Record<EmailTemplateDocumentType, string> = {
  Arbeitsbericht: DEFAULT_WORK_REPORT_SIGNER_EMAIL_SUBJECT_TEMPLATE,
};

const DEFAULT_BODY_BY_TYPE: Record<EmailTemplateDocumentType, string> = {
  Arbeitsbericht: DEFAULT_WORK_REPORT_SIGNER_EMAIL_BODY_TEMPLATE,
};

export function getDefaultDocumentEmailSubjectTemplate(documentType: EmailTemplateDocumentType): string {
  return DEFAULT_SUBJECT_BY_TYPE[documentType];
}

export function getDefaultDocumentEmailBodyTemplate(documentType: EmailTemplateDocumentType): string {
  return DEFAULT_BODY_BY_TYPE[documentType];
}

function buildSignerGreeting(signerName: string): string {
  const name = String(signerName ?? '').trim();
  if (!name) return 'Sehr geehrte Damen und Herren,';
  return `Guten Tag ${name},`;
}

function applySubjectPlaceholders(text: string, values: DocumentEmailTemplateValues): string {
  let out = replacePlaceholder(text, 'documentReference', values.documentReference);
  out = replacePlaceholder(out, 'dateLabel', values.dateLabel);
  out = replacePlaceholder(out, 'customerName', values.customerName);
  return replacePlaceholder(out, 'signerName', values.signerName);
}

function applyBodyPlaceholders(text: string, values: DocumentEmailTemplateValues): string {
  const withGreeting = replacePlaceholder(text, 'greeting', buildSignerGreeting(values.signerName));
  return applySubjectPlaceholders(withGreeting, values);
}

export function applyDocumentEmailSubjectTemplate(
  documentType: EmailTemplateDocumentType,
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
): string {
  const t = (template?.trim() || getDefaultDocumentEmailSubjectTemplate(documentType)).trim();
  return applySubjectPlaceholders(t, values);
}

export function applyDocumentEmailBodyTemplate(
  documentType: EmailTemplateDocumentType,
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
): string {
  const t = (template?.trim() || getDefaultDocumentEmailBodyTemplate(documentType)).trim();
  return applyBodyPlaceholders(t, values);
}

/** Apply checklist signer subject placeholders (fixed defaults; not Arbeitsbericht templates). */
export function applyChecklistSignerEmailSubjectTemplate(
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
): string {
  const t = (template?.trim() || DEFAULT_CHECKLIST_SIGNER_EMAIL_SUBJECT_TEMPLATE).trim();
  return applySubjectPlaceholders(t, values);
}

export function applyChecklistSignerEmailBodyTemplate(
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
): string {
  const t = (template?.trim() || DEFAULT_CHECKLIST_SIGNER_EMAIL_BODY_TEMPLATE).trim();
  return applyBodyPlaceholders(t, values);
}

/** Apply invoice signer subject placeholders (fixed defaults; not Arbeitsbericht templates). */
export function applyInvoiceSignerEmailSubjectTemplate(
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
): string {
  const t = (template?.trim() || DEFAULT_INVOICE_SIGNER_EMAIL_SUBJECT_TEMPLATE).trim();
  return applySubjectPlaceholders(t, values);
}

export function applyInvoiceSignerEmailBodyTemplate(
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
): string {
  const t = (template?.trim() || DEFAULT_INVOICE_SIGNER_EMAIL_BODY_TEMPLATE).trim();
  return applyBodyPlaceholders(t, values);
}

export function isValidSignerEmail(raw: unknown): boolean {
  const email = String(raw ?? '').trim().toLowerCase();
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** @deprecated Use applyDocumentEmailSubjectTemplate */
export const applyWorkReportSignerEmailSubjectTemplate = (
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
) => applyDocumentEmailSubjectTemplate(WORK_REPORT_DOCUMENT_TYPE, template, values);

/** @deprecated Use applyDocumentEmailBodyTemplate */
export const applyWorkReportSignerEmailBodyTemplate = (
  template: string | null | undefined,
  values: DocumentEmailTemplateValues,
) => applyDocumentEmailBodyTemplate(WORK_REPORT_DOCUMENT_TYPE, template, values);
